// ─── Dummy data types & fixtures for Consultation module ───

import {
  addMoney,
  multiplyMoney,
  percentageOf,
  phpFromMajor,
  phpZero,
  subtractMoney,
  toMajor,
} from '@helios/domain';

export type FileKind = 'image' | 'document';
export type FileTag = 'images' | 'documents' | 'consent' | 'lab' | 'before_after' | 'other';

export interface PatientFileItem {
  id: string;
  patientId: string;
  kind: FileKind;
  filename: string;
  url?: string;
  tag: FileTag;
  createdAt: string;
  starred: boolean;
  mimeType?: string;
  description?: string;
}

export interface PatientNoteItem {
  id: string;
  patientId: string;
  title: string;
  body: string;
  updatedAt: string;
}

export interface PatientFormItem {
  id: string;
  patientId: string;
  name: string;
  tag: 'consent' | 'lab' | 'other';
  createdAt: string;
}

// ─── Filter chip labels ───

export const FILE_FILTER_CHIPS = [
  'All',
  'Favorites',
  'Images',
  'Documents',
  'Consent',
  'Lab',
  'Before/After',
  'Other',
] as const;

export type FileFilterChip = (typeof FILE_FILTER_CHIPS)[number];

export const chipToTag: Record<FileFilterChip, FileTag | null> = {
  All: null,
  Favorites: null,
  Images: 'images',
  Documents: 'documents',
  Consent: 'consent',
  Lab: 'lab',
  'Before/After': 'before_after',
  Other: 'other',
};

// ─── Dummy image files ───

export function getDummyFiles(patientId: string): PatientFileItem[] {
  return [
    {
      id: 'f1',
      patientId,
      kind: 'image',
      filename: 'dental-impression.jpg',
      url: 'https://images.unsplash.com/photo-1606811841689-23dfddce3e95?w=400&h=300&fit=crop',
      tag: 'other',
      createdAt: '2026-02-18T10:40:00Z',
      starred: false,
    },
    {
      id: 'f2',
      patientId,
      kind: 'image',
      filename: 'intraoral-examination.jpg',
      url: 'https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?w=400&h=300&fit=crop',
      tag: 'other',
      createdAt: '2026-02-18T10:35:00Z',
      starred: false,
    },
    {
      id: 'f3',
      patientId,
      kind: 'image',
      filename: 'teeth-before-whitening.jpg',
      url: 'https://images.unsplash.com/photo-1598256989800-fe5f95da9787?w=400&h=300&fit=crop',
      tag: 'before_after',
      createdAt: '2026-02-18T10:30:00Z',
      starred: false,
    },
    {
      id: 'f4',
      patientId,
      kind: 'image',
      filename: 'panoramic-xray.jpg',
      url: 'https://images.unsplash.com/photo-1551190822-a9ce113ac100?w=400&h=300&fit=crop',
      tag: 'lab',
      createdAt: '2026-02-17T14:20:00Z',
      starred: false,
    },
    {
      id: 'f5',
      patientId,
      kind: 'image',
      filename: 'crown-placement-after.jpg',
      url: 'https://images.unsplash.com/photo-1609840114035-3c981b782dfe?w=400&h=300&fit=crop',
      tag: 'before_after',
      createdAt: '2026-02-16T09:15:00Z',
      starred: true,
    },
    {
      id: 'f6',
      patientId,
      kind: 'image',
      filename: 'periapical-radiograph.jpg',
      url: 'https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=400&h=300&fit=crop',
      tag: 'lab',
      createdAt: '2026-02-15T11:00:00Z',
      starred: true,
    },
    // Documents
    {
      id: 'f7',
      patientId,
      kind: 'document',
      filename: 'Treatment-Plan-2026.pdf',
      tag: 'documents',
      createdAt: '2026-02-18T09:00:00Z',
      starred: false,
      mimeType: 'application/pdf',
    },
    {
      id: 'f8',
      patientId,
      kind: 'document',
      filename: 'Informed-Consent-Extraction.pdf',
      tag: 'consent',
      createdAt: '2026-02-17T15:30:00Z',
      starred: true,
      mimeType: 'application/pdf',
    },
    {
      id: 'f9',
      patientId,
      kind: 'document',
      filename: 'Lab-Report-CBC.pdf',
      tag: 'lab',
      createdAt: '2026-02-16T08:00:00Z',
      starred: false,
      mimeType: 'application/pdf',
    },
    {
      id: 'f10',
      patientId,
      kind: 'document',
      filename: 'Referral-Letter-Ortho.docx',
      tag: 'other',
      createdAt: '2026-02-15T13:45:00Z',
      starred: false,
      mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    },
  ];
}

// ─── Dummy notes ───

export function getDummyNotes(patientId: string): PatientNoteItem[] {
  return [
    {
      id: 'n1',
      patientId,
      title: 'Initial Assessment',
      body: 'Patient presents with mild gingivitis in the lower anterior region. Recommended professional cleaning and improved home care routine. Patient reports sensitivity to cold on tooth #14.',
      updatedAt: '2026-02-18T10:45:00Z',
    },
    {
      id: 'n2',
      patientId,
      title: 'Follow-up: Crown Prep #19',
      body: 'Prepared tooth #19 for PFM crown. Impression taken with polyvinyl siloxane. Temporary crown placed. Patient tolerated procedure well. Return in 2 weeks for permanent crown.',
      updatedAt: '2026-02-15T14:20:00Z',
    },
    {
      id: 'n3',
      patientId,
      title: 'Orthodontic Consultation Notes',
      body: 'Discussed treatment options for mild crowding in lower arch. Patient interested in clear aligners. Referred to Dr. Martinez for evaluation and treatment planning.',
      updatedAt: '2026-02-10T09:30:00Z',
    },
    {
      id: 'n4',
      patientId,
      title: 'Post-Op: Extraction #32',
      body: 'Patient returned for post-operative check after wisdom tooth extraction. Healing well. No signs of dry socket. Advised to continue soft diet for 2 more days.',
      updatedAt: '2026-02-05T11:15:00Z',
    },
  ];
}

// ─── Dummy forms ───

export function getDummyForms(patientId: string): PatientFormItem[] {
  return [
    {
      id: 'fm1',
      patientId,
      name: 'Informed Consent — Tooth Extraction',
      tag: 'consent',
      createdAt: '2026-02-18T09:15:00Z',
    },
    {
      id: 'fm2',
      patientId,
      name: 'Informed Consent — Root Canal',
      tag: 'consent',
      createdAt: '2026-02-15T10:00:00Z',
    },
    {
      id: 'fm3',
      patientId,
      name: 'CBC Lab Order',
      tag: 'lab',
      createdAt: '2026-02-14T08:30:00Z',
    },
    {
      id: 'fm4',
      patientId,
      name: 'Orthodontic Referral Form',
      tag: 'other',
      createdAt: '2026-02-10T14:00:00Z',
    },
    {
      id: 'fm5',
      patientId,
      name: 'Dental Clearance Form',
      tag: 'other',
      createdAt: '2026-02-08T11:30:00Z',
    },
  ];
}

// ─── Invoice types ───

export type InvoiceStatus = 'draft' | 'issued' | 'paid';

/**
 * PRD.228 — Per-row discount type.
 *   - 'fixed'      → `discount` is a peso amount (legacy default).
 *   - 'percentage' → `discount` is a percent 0..100, applied to row subtotal.
 */
export type DiscountType = 'fixed' | 'percentage';

export interface InvoiceItem {
  id: string;
  serviceName: string;
  qty: number;
  unitPrice: number;
  discount: number;
  /** PRD.228 — defaults to 'fixed' when reading legacy rows. */
  discountType: DiscountType;
  taxRate: number;
}

export interface Invoice {
  id: string;
  invoiceNo: string;
  patientId: string;
  patientName: string;
  dateISO: string;
  status: InvoiceStatus;
  notes?: string;
  items: InvoiceItem[];
  createdAtISO: string;
  updatedAtISO: string;
}

// ─── Invoice computation helpers ───
//
// All arithmetic flows through Dinero so cumulative line-item math never
// drifts into JS float-precision land (e.g. 0.1 + 0.2 = 0.30000…04). The
// return type stays `number` (decimal-major) so call sites — invoice
// editor totals, treatment-record charged-cents conversion, etc. — keep
// working without changes. Storage units are unchanged; this is purely
// a calculation-layer refactor (parked migration to integer cents).
//
// PRD.228 — Row discount honors `item.discountType`:
//   - 'fixed'      → discount = peso amount (clamped 0..rowSubtotal)
//   - 'percentage' → discount = rowSubtotal × pct (pct clamped 0..100)
// `rowDiscountAmount` is the single source of truth for the effective
// discount in money terms; both row totals and the invoice summary use
// it so the displayed Subtotal/Discount/Tax/Total stay internally
// consistent regardless of mode.

/** Row subtotal (Money): qty × unitPrice, BEFORE any discount or tax. */
function rowSubtotal(item: InvoiceItem) {
  return multiplyMoney(phpFromMajor(item.unitPrice), item.qty);
}

/**
 * Effective discount for a row, expressed as Money, after honoring
 * `discountType` and the PRD.228 §8 clamping rules:
 *   - percentage clamped to [0, 100]
 *   - fixed clamped to [0, rowSubtotal]
 *   - missing / non-finite values treated as zero
 */
function rowDiscountAmount(item: InvoiceItem) {
  const subtotal = rowSubtotal(item);
  const subtotalCents = subtotal.getAmount();
  const raw = Number.isFinite(item.discount) ? item.discount : 0;
  if (item.discountType === 'percentage') {
    const clampedPct = Math.max(0, Math.min(100, raw));
    return percentageOf(subtotal, clampedPct);
  }
  // 'fixed' (or legacy undefined → treated as fixed by callers)
  const requested = phpFromMajor(raw);
  // Clamp at rowSubtotal so the row total can never go negative.
  if (requested.getAmount() > subtotalCents) return subtotal;
  if (requested.isNegative()) return phpZero();
  return requested;
}

/** Money base for a row before tax: subtotal − effective discount. */
function rowBase(item: InvoiceItem) {
  return subtractMoney(rowSubtotal(item), rowDiscountAmount(item));
}

export function rowTaxAmount(item: InvoiceItem): number {
  return toMajor(percentageOf(rowBase(item), item.taxRate));
}

export function rowTotal(item: InvoiceItem): number {
  const base = rowBase(item);
  return toMajor(addMoney(base, percentageOf(base, item.taxRate)));
}

/**
 * Effective discount in major units for a single row. Exposed so the
 * invoice editor can render the live applied amount alongside the raw
 * input (e.g. "10% → ₱25.00").
 */
export function rowDiscountApplied(item: InvoiceItem): number {
  return toMajor(rowDiscountAmount(item));
}

export function invoiceSummary(items: InvoiceItem[]) {
  let subtotal = phpZero();
  let discountTotal = phpZero();
  let taxTotal = phpZero();

  for (const i of items) {
    subtotal = addMoney(subtotal, rowSubtotal(i));
    discountTotal = addMoney(discountTotal, rowDiscountAmount(i));
    taxTotal = addMoney(taxTotal, percentageOf(rowBase(i), i.taxRate));
  }
  const grandTotal = addMoney(subtractMoney(subtotal, discountTotal), taxTotal);

  return {
    subtotal: toMajor(subtotal),
    discountTotal: toMajor(discountTotal),
    taxTotal: toMajor(taxTotal),
    grandTotal: toMajor(grandTotal),
  };
}

// ─── Dummy invoices ───

export function getDummyInvoices(patientId: string, patientName: string): Invoice[] {
  return [
    {
      id: 'inv1',
      invoiceNo: 'INV-00021',
      patientId,
      patientName,
      dateISO: '2026-03-16',
      status: 'draft',
      notes: '',
      items: [
        {
          id: 'ii1',
          serviceName: 'Consultation',
          qty: 1,
          unitPrice: 1500,
          discount: 0,
          discountType: 'fixed',
          taxRate: 0,
        },
      ],
      createdAtISO: '2026-03-16T09:00:00Z',
      updatedAtISO: '2026-03-16T09:00:00Z',
    },
    {
      id: 'inv2',
      invoiceNo: 'INV-00020',
      patientId,
      patientName,
      dateISO: '2026-03-10',
      status: 'paid',
      notes: 'Paid via GCash',
      items: [
        {
          id: 'ii2',
          serviceName: 'Consultation',
          qty: 1,
          unitPrice: 1500,
          discount: 0,
          discountType: 'fixed',
          taxRate: 0,
        },
        {
          id: 'ii3',
          serviceName: 'Dental Cleaning',
          qty: 1,
          unitPrice: 500,
          discount: 0,
          discountType: 'fixed',
          taxRate: 0,
        },
      ],
      createdAtISO: '2026-03-10T10:30:00Z',
      updatedAtISO: '2026-03-10T11:00:00Z',
    },
    {
      id: 'inv3',
      invoiceNo: 'INV-00019',
      patientId,
      patientName,
      dateISO: '2026-02-18',
      status: 'issued',
      notes: '',
      items: [
        {
          id: 'ii4',
          serviceName: 'Consultation',
          qty: 1,
          unitPrice: 1250,
          discount: 0,
          discountType: 'fixed',
          taxRate: 0,
        },
      ],
      createdAtISO: '2026-02-18T14:00:00Z',
      updatedAtISO: '2026-02-18T14:00:00Z',
    },
  ];
}

// ─── Helpers ───

const TAG_LABELS: Record<string, string> = {
  images: 'image',
  documents: 'document',
  consent: 'consent',
  lab: 'lab',
  before_after: 'before',
  other: 'other',
};

export function tagLabel(tag: string): string {
  return TAG_LABELS[tag] ?? tag;
}

export function formatDateTime(iso: string): string {
  const d = new Date(iso);
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const h = d.getHours();
  const m = d.getMinutes().toString().padStart(2, '0');
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()} ${h12}:${m} ${ampm}`;
}

export function formatDateShort(iso: string): string {
  const d = new Date(iso);
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}
